import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, AlertCircle, CheckCircle2 } from 'lucide-react';
import { apiClient, ApiError } from '../lib/apiClient';
import { useAuthStore } from '../store/authStore';

interface PolicyIssue {
  code: string;
  message: string;
}

const REQS: { id: string; label: string; test: (s: string) => boolean }[] = [
  { id: 'len', label: 'At least 12 characters', test: (s) => s.length >= 12 },
  { id: 'lower', label: 'One lowercase letter', test: (s) => /[a-z]/.test(s) },
  { id: 'upper', label: 'One uppercase letter', test: (s) => /[A-Z]/.test(s) },
  { id: 'digit', label: 'One digit', test: (s) => /\d/.test(s) },
  { id: 'symbol', label: 'One symbol', test: (s) => /[^A-Za-z0-9]/.test(s) },
];

export default function ChangePassword() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const hydrate = useAuthStore((s) => s.hydrate);

  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const allOk = REQS.every((r) => r.test(newPassword)) && newPassword === confirm && currentPassword.length > 0;

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirm) {
      setError('New passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await apiClient.post<{ ok: boolean }>('/auth/password/change', { currentPassword, newPassword });
      // Server revoked all sessions — refresh auth without toggling global
      // `loading` (that would unmount this page and break navigation).
      await hydrate({ silent: true });
      navigate('/login', { replace: true, state: { changed: true } });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'BAD_REQUEST' && Array.isArray(err.details)) {
        setError((err.details as PolicyIssue[]).map((d) => d.message).join(' '));
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Could not change password.');
      }
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    'w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all';

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-brand-500 flex items-center justify-center">
            <ShieldCheck className="text-white" size={20} />
          </div>
          <div>
            <h1 className="text-white font-bold text-xl">Set a new password</h1>
            <p className="text-gray-400 text-sm">Required before you continue, {user?.name?.split(' ')[0] ?? ''}.</p>
          </div>
        </div>

        <form onSubmit={handle} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Current (temporary) password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">New password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNew(e.target.value)}
              autoComplete="new-password"
              required
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Confirm new password</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              className={inputCls}
            />
          </div>

          <ul className="space-y-1 mt-2 text-xs">
            {REQS.map((r) => {
              const ok = r.test(newPassword);
              return (
                <li
                  key={r.id}
                  className={`flex items-center gap-2 ${ok ? 'text-green-400' : 'text-gray-500'}`}
                >
                  <CheckCircle2 size={12} className={ok ? 'text-green-400' : 'text-gray-600'} />
                  {r.label}
                </li>
              );
            })}
            {confirm.length > 0 && (
              <li
                className={`flex items-center gap-2 ${
                  newPassword === confirm ? 'text-green-400' : 'text-gray-500'
                }`}
              >
                <CheckCircle2
                  size={12}
                  className={newPassword === confirm ? 'text-green-400' : 'text-gray-600'}
                />
                Passwords match
              </li>
            )}
          </ul>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-900/30 border border-red-800 rounded-lg">
              <AlertCircle size={15} className="text-red-400 shrink-0 mt-0.5" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={!allOk || busy}
            className="w-full py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg text-sm transition-colors"
          >
            {busy ? 'Updating…' : 'Set new password'}
          </button>
          <p className="text-xs text-gray-500 text-center">
            You'll be signed out of all devices and asked to sign in again.
          </p>
        </form>
      </div>
    </div>
  );
}
